<template>
  <div class="estadisticas-generales mt-3">
    <button class="btn btn-toggle mb-3" @click="plegado = !plegado">
      <font-awesome-icon :icon="['fas', 'caret-down']" :class="{ 'rotado': !plegado }" />
      {{ plegado ? 'Mostrar estadísticas del proyecto' : 'Ocultar estadísticas del proyecto' }}
    </button>

    <div v-show="!plegado" class="contenido-estadisticas">

      <!-- RESUMEN GENERAL -->
      <div class="row small">
        <div class="col-md-4"><strong>Actividades planificadas:</strong> {{ planificadas.length }}</div>
        <div class="col-md-4"><strong>Tareas ejecutadas planificadas:</strong> {{ ejecutadasPlanificadas.length }}</div>
        <div class="col-md-4"><strong>Tareas ejecutadas no planificadas:</strong> {{ ejecutadasNoPlanificadas.length }}</div>
      </div>

      <hr class="my-3">

      <div class="row small">
        <div class="col-md-4"><strong>Total planificadas:</strong> {{ planificadas.length }}</div>
        <div class="col-md-4"><strong>Total ejecutadas:</strong> {{ totalEjecutadas }}</div>
        <div class="col-md-4"><strong>Total global:</strong> {{ totalGlobal }}</div>
      </div>

      <div class="row small mt-2">
        <div class="col-md-6"><strong>Total estimado:</strong> {{ simbolo }}{{ totalEstimado }}</div>
        <div class="col-md-6"><strong>Total real:</strong> {{ simbolo }}{{ totalReal }}</div>
      </div>

      <div class="row small mt-1 mb-3">
        <div class="col-md-12"><strong>Desviación:</strong>
          <span :class="totalDesviacion > 0 ? 'text-danger fw-bold' : 'text-success fw-bold'">
            {{ simbolo }}{{ totalDesviacion.toFixed(2) }}
          </span>
        </div>
      </div>

      <!-- PLANIFICADAS VS NO -->
      <div class="row g-4 mb-4">
        <div class="col-md-4">
          <div class="card shadow-sm p-3 border-success border-start border-4">
            <h6 class="text-success d-flex align-items-center gap-2">
              <font-awesome-icon icon="calendar-check" />
              Planificadas Ejecutadas
              <span class="badge bg-success ms-auto">{{ planificadasEjecutadas.length }}</span>
            </h6>
            <ul class="list-unstyled small mt-2">
              <li><strong>Estimado:</strong> {{ simbolo }}{{ suma(planificadasEjecutadas, 'costo_estimado_usd') }}</li>
              <li><strong>Real:</strong> {{ simbolo }}{{ suma(planificadasEjecutadas, 'costo_real_usd') }}</li>
            </ul>
          </div>
        </div>

        <div class="col-md-4">
          <div class="card shadow-sm p-3 border-secondary border-start border-4 bg-light">
            <h6 class="text-secondary d-flex align-items-center gap-2">
              <font-awesome-icon icon="clock" />
              Planificadas sin ejecutar
              <span class="badge bg-secondary ms-auto">{{ planificadasSinEjecucion.length }}</span>
            </h6>
            <ul class="list-unstyled small mt-2">
              <li><strong>Estimado:</strong> {{ simbolo }}{{ suma(planificadasSinEjecucion, 'costo_estimado_usd') }}</li>
              <li><strong>Real:</strong> -</li>
            </ul>
          </div>
        </div>

        <div class="col-md-4">
          <div class="card shadow-sm p-3 border-warning border-start border-4 bg-light">
            <h6 class="text-warning d-flex align-items-center gap-2">
              <font-awesome-icon icon="triangle-exclamation" />
              Ejecutadas No Planificadas
              <span class="badge bg-warning text-dark ms-auto">{{ ejecutadasNoPlanificadas.length }}</span>
            </h6>
            <ul class="list-unstyled small mt-2">
              <li><strong>Estimado:</strong> -</li>
              <li><strong>Real:</strong> {{ simbolo }}{{ suma(ejecutadasNoPlanificadas, 'costo_real_usd') }}</li>
            </ul>
          </div>
        </div>
      </div>

      <!-- POR ETAPA -->
      <div class="row g-4">
        <div class="col-md-6" v-for="(grupo, i) in resumenPorEtapa" :key="i">
          <div class="card p-3 shadow-sm border-start border-4"
            :class="grupo.total_desviacion > 0 ? 'border-danger bg-light' : 'border-success bg-light'">
            <h6 class="text-dark d-flex align-items-center gap-2">
              <font-awesome-icon icon="seedling" />
              Etapa: {{ grupo.etapa }}
              <span v-if="grupo.total_desviacion > 0" class="badge bg-danger ms-auto">⚠ Desviación</span>
            </h6>
            <ul class="list-unstyled small mt-2">
              <li><strong>Actividades:</strong> {{ grupo.total_actividades }}</li>
              <li><strong>Estimado:</strong> {{ simbolo }}{{ grupo.total_estimado.toFixed(2) }}</li>
              <li><strong>Real:</strong> {{ simbolo }}{{ grupo.total_real.toFixed(2) }}</li>
              <li><strong>Desviación:</strong>
                <span :class="grupo.total_desviacion > 0 ? 'text-danger fw-bold' : 'text-success fw-bold'">
                  {{ simbolo }}{{ grupo.total_desviacion.toFixed(2) }}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

const props = defineProps({
  planificadas: Array,
  ejecutadasPlanificadas: Array,
  ejecutadasNoPlanificadas: Array,
  simbolo: { type: String, default: '$' }
})

const plegado = ref(false)

const planificadasEjecutadas = computed(() => props.ejecutadasPlanificadas || [])

const planificadasSinEjecucion = computed(() => {
  const ejecutadasIds = new Set(props.ejecutadasPlanificadas.map(t => t.plan_cultivo_actividades_id))
  return props.planificadas.filter(p => !ejecutadasIds.has(p.id))
})

const totalEjecutadas = computed(() =>
  props.ejecutadasPlanificadas.length + props.ejecutadasNoPlanificadas.length
)

const totalGlobal = computed(() =>
  props.planificadas.length + totalEjecutadas.value
)

const totalEstimado = computed(() =>
  Number(suma(planificadasEjecutadas.value, 'costo_estimado_usd')) +
  Number(suma(planificadasSinEjecucion.value, 'costo_estimado_usd'))
)

const totalReal = computed(() =>
  Number(suma(planificadasEjecutadas.value, 'costo_real_usd')) +
  Number(suma(props.ejecutadasNoPlanificadas, 'costo_real_usd'))
)

const totalDesviacion = computed(() => totalReal.value - totalEstimado.value)

const resumenPorEtapa = computed(() => {
  const agrupado = {};
  const actividades = [...planificadasEjecutadas.value, ...planificadasSinEjecucion.value];

  actividades.forEach(a => {
    const key = a.etapa_evento_cultivo || 'Sin etapa';
    if (!agrupado[key]) {
      agrupado[key] = { etapa: key, total_actividades: 0, total_estimado: 0, total_real: 0 };
    }
    agrupado[key].total_actividades++;
    agrupado[key].total_estimado += a.costo_estimado_usd || 0;
    agrupado[key].total_real += a.costo_real_usd ?? a.costo_final_usd ?? 0;
  });

  return Object.values(agrupado).map(g => ({
    ...g,
    total_desviacion: g.total_real - g.total_estimado
  }));
})

function suma(arr, campo) {
  return arr.reduce((acc, a) => {
    const val = a[campo] ?? (campo === 'costo_real_usd' ? a.costo_final_usd : 0);
    return acc + (val || 0);
  }, 0).toFixed(2);
}
</script>

<style scoped>
.btn-toggle {
  background-color: #f8f9fa;
  border: 1px solid #ced4da;
  font-weight: 600;
  font-size: 0.95rem;
  padding: 0.4rem 0.75rem;
}
.rotado {
  transform: rotate(180deg);
  transition: transform 0.3s ease;
}
</style>
